// react-scan must be imported before React.
// In production builds, vite.config.ts aliases this to a no-op stub.
import { scan } from "react-scan";

if (import.meta.env.DEV) {
    scan({ enabled: false, showToolbar: true });
}

// ensure to bind ipc as first thing
import "@/ipc";

// Initialize Sentry before app.tsx so tracing is active before the router is created
import "@/sentry";

// then import other things
import "@/index.css";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ConfigCatProvider } from "configcat-react";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import React from "react";
import ReactDOM from "react-dom/client";

import { GitpodAPI } from "@/api";
import { App } from "@/app";
import { ExceptionErrorBoundary } from "@/components/ExceptionHandler";
import { Toaster } from "@/components/flexkit/toast";
import { QueryClientProvider } from "@/components/QueryClientProvider";
import { printConsoleEasterEgg } from "@/console-easter-egg";
import { ConfettiContextProvider } from "@/contexts/ConfettiContext";
import { DesktopProvider } from "@/contexts/DesktopProvider";
import { GitpodAPIContext } from "@/contexts/GitpodAPIContext";
import { createSegment } from "@/contexts/SegmentContext";
import { SegmentProvider } from "@/contexts/SegmentProvider";
import { ShareResourceModalProvider } from "@/contexts/ShareResourceContext";
import { ThemeProvider } from "@/contexts/ThemeProvider";
import { localFlagOverrides } from "@/feature-flags";
import { appMainService, runnerMainService } from "@/ipc";
import { restorePrincipal } from "@/principal";
import { startVersionTracker } from "@/version-tracker";

// Restoring principal from local storage before warming up the query client here.
restorePrincipal();

printConsoleEasterEgg();

// Start tracking version of the management plane
startVersionTracker();

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <QueryClientProvider>
            <SegmentProvider create={() => createSegment()}>
                <ConfigCatProvider
                    sdkKey={"configcat-proxy/default"}
                    options={{
                        baseUrl: window.origin + "/feature-flags/configcat",
                        flagOverrides: localFlagOverrides(),
                        setupHooks: (hooks) => hooks.on("clientReady", () => console.log("ConfigCat client is ready!")),
                    }}
                >
                    <DesktopProvider
                        runnerService={runnerMainService}
                        isDesktop={!!runnerMainService}
                        appMainService={appMainService}
                    >
                        <GitpodAPIContext.Provider value={GitpodAPI}>
                            {/* Currently the ThemeProvider needs to be a child of the API provider, as we rely on the users identity to determine the theme based on our feature flag settings */}
                            <ThemeProvider>
                                <ExceptionErrorBoundary>
                                    <ConfettiContextProvider>
                                        <ShareResourceModalProvider>
                                            <NuqsAdapter>
                                                <div className="fixed bottom-3 right-20 z-[99999]">
                                                    <ReactQueryDevtools
                                                        initialIsOpen={false}
                                                        buttonPosition="relative"
                                                    />
                                                </div>
                                                <App />
                                                <Toaster />
                                            </NuqsAdapter>
                                        </ShareResourceModalProvider>
                                    </ConfettiContextProvider>
                                </ExceptionErrorBoundary>
                            </ThemeProvider>
                        </GitpodAPIContext.Provider>
                    </DesktopProvider>
                </ConfigCatProvider>
            </SegmentProvider>
        </QueryClientProvider>
    </React.StrictMode>,
);
